// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Spin from 'antd/lib/spin';
import Result from 'antd/lib/result';
import notification from 'antd/lib/notification';

import { getRunDetailAsync, updateJobQAStateAsync } from 'actions/labeling-runs-actions';
import { CombinedState } from 'reducers';
import TopBarComponent from './top-bar';
import JobListComponent from './job-list';

export default function RunTaskPage(): JSX.Element {
    const { runId: runIdStr, taskId: taskIdStr } = useParams<{ runId: string; taskId: string }>();
    const runId = parseInt(runIdStr, 10);
    const taskId = parseInt(taskIdStr, 10);
    const dispatch = useDispatch();
    const qaStates = useSelector((state: CombinedState) => state.activeRun.qaStates);
    const run = useSelector((state: CombinedState) => state.activeRun.run);
    const [taskInstance, setTaskInstance] = useState<any>(null);
    const [fetching, setFetching] = useState(true);
    const autoSyncedRef = useRef<string | null>(null);

    useEffect(() => {
        const runDetailPromise = dispatch(getRunDetailAsync(runId) as any);
        const jobsPromise = fetch(`/api/labeling-runs/${runId}/task-jobs/${taskId}`, {
            credentials: 'same-origin',
        }).then((res) => {
            if (!res.ok) throw new Error(`${res.status}`);
            return res.json();
        }).then((jobs: any[]) => {
            setTaskInstance({
                id: taskId,
                jobs: jobs.map((j: any) => ({
                    id: j.id,
                    startFrame: j.start_frame,
                    stopFrame: j.stop_frame,
                    assignee: j.assignee,
                    stage: j.stage,
                    state: j.state,
                    updatedDate: j.updated_date,
                })),
            });
        }).catch((error: Error) => {
            notification.error({ message: 'Could not load jobs', description: error.toString() });
        });

        Promise.all([runDetailPromise, jobsPromise]).finally(() => setFetching(false));
    }, [runId, taskId]);

    // Auto-sync: once per (runId, taskId) load, after both task and qaStates are ready
    useEffect(() => {
        const syncKey = `${runId}:${taskId}`;
        if (!taskInstance || fetching || autoSyncedRef.current === syncKey) return;
        autoSyncedRef.current = syncKey;
        taskInstance.jobs.forEach((job: any) => {
            const currentQA = qaStates[job.id] || 'new';
            if (job.stage === 'annotation' && job.state === 'in progress' && currentQA === 'new') {
                dispatch(updateJobQAStateAsync(runId, job.id, 'labeling') as any);
            } else if (job.stage === 'acceptance' && job.state === 'completed' &&
                       (currentQA === 'new' || currentQA === 'labeling')) {
                dispatch(updateJobQAStateAsync(runId, job.id, 'done') as any);
            }
        });
    }, [taskInstance, qaStates, fetching]);

    if (fetching) {
        return <Spin size='large' className='cvat-spinner' />;
    }

    if (!taskInstance) {
        return (
            <Result
                className='cvat-not-found'
                status='404'
                title='Task not found'
            />
        );
    }

    return (
        <div className='cvat-run-task-page' style={{ paddingTop: 16, height: '100%', overflow: 'auto' }}>
            <TopBarComponent task={taskInstance} runId={runId} />
            <JobListComponent task={taskInstance} runId={runId} qaStates={qaStates} />
        </div>
    );
}
